import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { LoadingSpinner, PrimaryBlueButton, Severity } from "@velaro/velaro-component-lib";
import useAIConfigs from "@/hooks/useAIConfigs";
import useToastAlert from "@/hooks/useToastAlert";
import useApi from "@/hooks/useApi";
import AINewSettingsTabs from "./AINewSettingsTabs";
import { channelDefs, Channel } from "@/types";
import { CHANNEL_SOURCE_MAP } from "../channelOverrideTypes";

interface PlaygroundOverride {
  id: number;
  topic: string;
  fact: string;
  searchTerms: string;
  priority: number;
  fired: boolean;
  reason: string;
}

interface PlaygroundVectorResult {
  title: string;
  snippet: string;
  contentType: string;
  sourceUrl: string;
}

interface PlaygroundKBArticle {
  id: number;
  title: string;
  snippet: string;
  matchReason: string;
}

interface PlaygroundDiagnostics {
  sources: string[];
  overrides: PlaygroundOverride[];
  vectorResults: PlaygroundVectorResult[] | null;
  kbArticles: PlaygroundKBArticle[];
  tokenEstimate: number;
  modelUsed: string;
  channelApplied?: string;
  channelOverrideMode?: string;
  channelOverrideText?: string;
}

interface ChatQueryResponse {
  success: boolean;
  answer: string;
  error?: string;
  diagnostics: PlaygroundDiagnostics;
}

const SOURCE_LABELS: Record<string, string> = {
  override: "Knowledge Overrides",
  vector: "Vector Index",
  kb: "KB Articles"
};

const SOURCE_COLORS: Record<string, string> = {
  override: "bg-amber-100 text-amber-800 border-amber-200",
  vector: "bg-blue-100 text-blue-800 border-blue-200",
  kb: "bg-green-100 text-green-800 border-green-200"
};

// Derive channel list from canonical sources rather than duplicating them
const CHANNELS = [
  { value: "", label: "No channel (base prompt only)" },
  ...Object.entries(CHANNEL_SOURCE_MAP).map(([adminKey, backendVal]) => ({
    value: backendVal,
    label: channelDefs[adminKey as Channel] ?? adminKey,
  })),
];

export default function AINewSettingsPlayground() {
  const { id } = useParams();
  const { aiConfigurations, loading } = useAIConfigs();
  const toastAlert = useToastAlert();
  const api = useApi();

  const [query, setQuery] = useState("");
  const [selectedChannel, setSelectedChannel] = useState("");
  const [answer, setAnswer] = useState<string | null>(null);
  const [diagnostics, setDiagnostics] = useState<PlaygroundDiagnostics | null>(null);
  const [querying, setQuerying] = useState(false);
  const [showDiagnostics, setShowDiagnostics] = useState(false);

  const config = aiConfigurations.find((c) => c.id === parseInt(id!));

  const handleQuery = async () => {
    if (!query.trim()) {
      toastAlert.displayToast(Severity.Error, "Query cannot be empty");
      return;
    }

    setQuerying(true);
    setAnswer(null);
    setDiagnostics(null);
    setShowDiagnostics(false);

    try {
      const response = await api.messaging.post("AzureIndexes/ChatQuery", {
        aiConfigId: parseInt(id!),
        query: query,
        channel: selectedChannel || null,
      });

      const data: ChatQueryResponse = await response.json();

      if (data.success) {
        setAnswer(data.answer);
        setDiagnostics(data.diagnostics ?? null);
      } else {
        toastAlert.displayToast(Severity.Error, data.error || "Failed to get response");
        setAnswer(`Error: ${data.error || "Failed to get response"}`);
      }
    } catch (error) {
      console.error("Query error:", error);
      toastAlert.displayToast(Severity.Error, "An error occurred while querying");
    } finally {
      setQuerying(false);
    }
  };

  const handleClear = () => {
    setAnswer(null);
    setDiagnostics(null);
    setQuery("");
    setShowDiagnostics(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  const firedOverrides = diagnostics?.overrides.filter((o) => o.fired) ?? [];
  const skippedOverrides = diagnostics?.overrides.filter((o) => !o.fired) ?? [];

  const channelLabel = CHANNELS.find((c) => c.value === selectedChannel)?.label ?? "No channel";

  return (
    <div className="flex flex-col">
      <AINewSettingsTabs aiConfigId={id!} />

      <div className="bg-white p-6 border rounded-2xl shadow-sm w-[750px] self-center mt-4">
        <div className="flex flex-col gap-2">
          {/* Active Index / Knowledge Sources */}
          <div className="py-3">
            <div className="font-semibold text-sm pb-1">Knowledge Sources</div>
            <div className="text-sm p-3 bg-slate-50 rounded-md flex flex-col gap-1">
              {config?.indexName ? (
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-500 inline-block"></span>
                  <span className="font-medium text-slate-700">Vector index:</span>
                  <span className="text-slate-500">{config.indexName}</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-amber-600">
                  <span className="w-2 h-2 rounded-full bg-amber-400 inline-block"></span>
                  No vector index configured — go to Training to set one up
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-amber-500 inline-block"></span>
                <span className="text-slate-500">Knowledge overrides always checked first</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
                <span className="text-slate-500">KB articles (if enabled) also included</span>
              </div>
            </div>
          </div>

          {/* Channel selector */}
          <div className="py-2">
            <div className="font-semibold text-sm pb-1">Test as channel</div>
            <div className="text-slate-500 text-sm pb-2">
              Select a channel to simulate how the bot responds under that channel's overrides and prompt additions.
            </div>
            <select
              className="w-full p-2 border rounded-md text-sm text-slate-700"
              value={selectedChannel}
              onChange={(e) => setSelectedChannel(e.target.value)}
            >
              {CHANNELS.map((ch) => (
                <option key={ch.value} value={ch.value}>
                  {ch.label}
                </option>
              ))}
            </select>
            {selectedChannel && (
              <p className="text-xs text-blue-600 mt-1">
                Channel override for <strong>{channelLabel}</strong> will be applied if configured under Channel Overrides.
              </p>
            )}
          </div>

          {/* Test Query */}
          <div className="py-3">
            <div className="font-semibold text-sm pb-1">Ask a question</div>
            <div className="text-slate-500 text-sm pb-2">
              Runs the same pipeline as a live chat — overrides, then your index, then KB articles. See the log below for what fired.
            </div>
            <textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleQuery();
                }
              }}
              placeholder="What would a visitor ask your bot?"
              rows={3}
              className="w-full p-2 border rounded-md text-sm resize-none"
            />
            <div className="text-xs text-slate-400 mt-1">
              Press Enter to send, Shift+Enter for new line
            </div>
          </div>

          <div className="flex gap-2">
            <PrimaryBlueButton
              label={querying ? "Thinking..." : "Test"}
              onClick={handleQuery}
              disabled={querying || !query.trim()}
            />
            {(answer !== null || query) && (
              <button
                onClick={handleClear}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-md transition-colors"
              >
                Clear
              </button>
            )}
          </div>

          {/* AI Response */}
          {answer !== null && (
            <div className="pt-4 flex flex-col gap-3">
              {/* Channel context badge */}
              {diagnostics?.channelApplied && (
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-1 rounded-full border bg-indigo-100 text-indigo-800 border-indigo-200 font-medium">
                    Channel: {CHANNELS.find((c) => c.value === diagnostics.channelApplied)?.label ?? diagnostics.channelApplied}
                  </span>
                  {diagnostics.channelOverrideMode && diagnostics.channelOverrideMode !== "none" && (
                    <span className="text-xs px-2 py-1 rounded-full border bg-violet-100 text-violet-800 border-violet-200 font-medium">
                      Override mode: {diagnostics.channelOverrideMode}
                    </span>
                  )}
                </div>
              )}

              {/* Sources badges */}
              {diagnostics && diagnostics.sources.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {diagnostics.sources.map((src) => (
                    <span
                      key={src}
                      className={`text-xs px-2 py-1 rounded-full border font-medium ${SOURCE_COLORS[src] ?? "bg-slate-100 text-slate-600"}`}
                    >
                      {SOURCE_LABELS[src] ?? src}
                    </span>
                  ))}
                </div>
              )}
              {diagnostics && diagnostics.sources.length === 0 && (
                <span className="text-xs px-2 py-1 rounded-full border bg-slate-100 text-slate-500 self-start">
                  No knowledge sources matched — bot answered from instructions only
                </span>
              )}

              <div>
                <div className="font-semibold text-sm pb-2">Bot response:</div>
                <div className="bg-slate-50 border rounded-lg p-4 max-h-64 overflow-auto">
                  <div className="text-sm text-slate-700 whitespace-pre-wrap">{answer}</div>
                </div>
              </div>

              {/* Diagnostic log toggle */}
              {diagnostics && (
                <button
                  onClick={() => setShowDiagnostics((v) => !v)}
                  className="text-xs text-slate-500 hover:text-slate-700 underline self-start"
                >
                  {showDiagnostics ? "Hide" : "Show"} source log
                  {diagnostics.tokenEstimate > 0 && (
                    <span className="ml-2 no-underline text-slate-400">
                      (~{diagnostics.tokenEstimate} tokens, model: {diagnostics.modelUsed})
                    </span>
                  )}
                </button>
              )}

              {/* Diagnostic log */}
              {showDiagnostics && diagnostics && (
                <div className="border rounded-lg overflow-hidden text-xs">
                  {/* Channel override explanation */}
                  {diagnostics.channelApplied && (
                    <div className="border-b">
                      <div className="bg-indigo-50 px-3 py-2 font-semibold text-indigo-800">
                        Channel context: {CHANNELS.find((c) => c.value === diagnostics.channelApplied)?.label ?? diagnostics.channelApplied}
                      </div>
                      <div className="px-3 py-2 bg-white text-slate-600">
                        {diagnostics.channelOverrideMode && diagnostics.channelOverrideMode !== "none" ? (
                          <>
                            <div className="mb-1">
                              Override mode: <span className="font-medium">{diagnostics.channelOverrideMode}</span>
                              {diagnostics.channelOverrideMode === "additive" && " — channel instructions added on top of base prompt"}
                              {diagnostics.channelOverrideMode === "replace" && " — channel instructions replaced the base prompt entirely"}
                              {diagnostics.channelOverrideMode === "suppress" && " — channel override suppressed the base prompt"}
                            </div>
                            {diagnostics.channelOverrideText && (
                              <div className="text-slate-400 mt-1 whitespace-pre-wrap border-l-2 border-indigo-200 pl-2">
                                {diagnostics.channelOverrideText}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-slate-400">No channel override configured for this channel — base prompt used as-is.</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Fired overrides */}
                  {firedOverrides.length > 0 && (
                    <div className="border-b">
                      <div className="bg-amber-50 px-3 py-2 font-semibold text-amber-800">
                        Knowledge overrides injected ({firedOverrides.length})
                      </div>
                      {firedOverrides.map((o) => (
                        <div key={o.id} className="px-3 py-2 border-b last:border-b-0 bg-white">
                          <div className="flex justify-between items-start">
                            <span className="font-medium text-slate-700">{o.topic}</span>
                            <span className="text-amber-600 ml-2 shrink-0">{o.reason}</span>
                          </div>
                          <div className="text-slate-500 mt-0.5">{o.fact}</div>
                          {o.searchTerms && (
                            <div className="text-slate-400 mt-0.5">keywords: {o.searchTerms}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Vector results */}
                  {diagnostics.vectorResults && diagnostics.vectorResults.length > 0 && (
                    <div className="border-b">
                      <div className="bg-blue-50 px-3 py-2 font-semibold text-blue-800">
                        Index results ({diagnostics.vectorResults.length})
                      </div>
                      {diagnostics.vectorResults.map((v, i) => (
                        <div key={i} className="px-3 py-2 border-b last:border-b-0 bg-white">
                          {v.title && <div className="font-medium text-slate-700">{v.title}</div>}
                          <div className="text-slate-500 mt-0.5">{v.snippet}</div>
                          {v.sourceUrl && (
                            <div className="text-slate-400 mt-0.5 truncate">{v.sourceUrl}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* KB Articles */}
                  {diagnostics.kbArticles.length > 0 && (
                    <div className="border-b">
                      <div className="bg-green-50 px-3 py-2 font-semibold text-green-800">
                        KB articles matched ({diagnostics.kbArticles.length})
                      </div>
                      {diagnostics.kbArticles.map((a) => (
                        <div key={a.id} className="px-3 py-2 border-b last:border-b-0 bg-white">
                          <div className="flex justify-between items-start">
                            <span className="font-medium text-slate-700">{a.title}</span>
                            <span className="text-green-600 ml-2 shrink-0">{a.matchReason}</span>
                          </div>
                          {a.snippet && <div className="text-slate-500 mt-0.5">{a.snippet}</div>}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Skipped overrides */}
                  {skippedOverrides.length > 0 && (
                    <div>
                      <div className="bg-slate-50 px-3 py-2 font-semibold text-slate-500">
                        Overrides seen but not matched ({skippedOverrides.length})
                      </div>
                      {skippedOverrides.map((o) => (
                        <div key={o.id} className="px-3 py-2 border-b last:border-b-0 bg-white text-slate-400">
                          <div className="flex justify-between items-start">
                            <span>{o.topic}</span>
                            <span className="ml-2 shrink-0">{o.reason}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Nothing matched */}
                  {diagnostics.sources.length === 0 && !diagnostics.channelApplied && (
                    <div className="px-3 py-3 text-slate-400 bg-white">
                      No knowledge sources matched this query. The bot responded from its base training only.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="w-[750px] self-center mt-4 p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-500">
        This test runs the same knowledge pipeline as a live conversation — overrides first, then the vector index, then KB articles. Select a channel above to simulate channel-specific prompt overrides. Results are not saved.
      </div>
    </div>
  );
}
